import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { CardMessage, CodeProposal, PromptContext } from '../types';
import { activeCard, activeCode, activeSpec, attach, modelLabel, providerReady, review, settings, streaming, suggestions, updateUi } from '../state/store';
import {
  acceptReview,
  editUserMessage,
  rejectReview,
  retryLastTurn,
  sendMessage,
  sendSuggestion,
  stopStreaming,
  toggleAttachment,
  updateSettings,
} from '../state/actions';
import { languageInfo } from '../languages';
import { outputItems, runFailed } from '../run/output';
import { specState, SPEC_STATE_LABEL } from '../util/spec';
import { Markdown } from './Markdown';
import { IconAlert, IconBook, IconCheck, IconCode, IconPencil, IconPlus, IconRefresh, IconSend, IconStop, IconTerminal, IconX } from './Icons';

const lineCount = (text: string) => text.split('\n').length;
const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

function proposalTitle(p: CodeProposal): string {
  const code = p.code !== undefined ? `${languageInfo(p.language ?? '').label} · ${plural(lineCount(p.code), 'line')}` : '';
  if (code && p.spec !== undefined) return `${code}, and the spec`;
  return code || 'Spec';
}

function ProposalCard({ proposal }: { proposal: CodeProposal }) {
  const isActiveReview = review.value?.proposalId === proposal.id;
  const statusLabel: Record<CodeProposal['status'], string> = {
    pending: 'Waiting for your review',
    accepted: 'Accepted',
    rejected: 'Rejected',
    superseded: 'Replaced',
  };
  return (
    <div class="proposal">
      <div class="proposal-head">
        {proposal.code !== undefined ? <IconCode size={16} /> : <IconBook size={16} />}
        <span class="proposal-title">{proposalTitle(proposal)}</span>
        <span class={`proposal-status status-chip is-${proposal.status}`}>{statusLabel[proposal.status]}</span>
      </div>
      {proposal.summary && <p class="proposal-summary">{proposal.summary}</p>}
      {proposal.status === 'pending' && isActiveReview && (
        <div class="proposal-actions">
          <button type="button" class="button primary small" onClick={acceptReview}>
            <IconCheck size={15} /> Accept all
          </button>
          <button type="button" class="button small" onClick={rejectReview}>
            <IconX size={15} /> Reject all
          </button>
          <button type="button" class="button ghost small" onClick={() => updateUi({ mobileTab: 'code', workspaceTab: proposal.code !== undefined ? 'code' : 'spec' })}>
            Review changes
          </button>
        </div>
      )}
    </div>
  );
}

function ContextChips({ context }: { context: PromptContext }) {
  const chips: string[] = [];
  if (context.code?.code.trim()) chips.push(languageInfo(context.code.language).label);
  if (context.spec?.text.trim()) chips.push('Spec');
  if (context.output?.text.trim()) chips.push(context.output.failed ? 'Failed run' : 'Run output');
  if (chips.length === 0) return null;
  return (
    <span class="chip" title="Sent along with this prompt">
      {chips.join(' · ')}
    </span>
  );
}

function UserMessage({ message }: { message: CardMessage }) {
  const [editing, setEditing] = useState(false);
  const text = message.parts
    .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
    .map((p) => p.text)
    .join('\n');
  const [draft, setDraft] = useState(text);
  const busy = streaming.value !== null;

  if (editing) {
    const resend = () => {
      setEditing(false);
      void editUserMessage(message.id, draft);
    };
    return (
      <div class="message user editing">
        <textarea
          class="edit-area"
          value={draft}
          aria-label="Edit prompt"
          rows={Math.min(12, Math.max(2, draft.split('\n').length))}
          onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setEditing(false);
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              resend();
            }
          }}
        />
        <div class="edit-actions">
          <button type="button" class="button primary small" onClick={resend}>
            Resend
          </button>
          <button type="button" class="button small" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }
  return (
    <div class="message user">
      <Markdown source={text} class="message-text" />
      <div class="message-foot">
        {message.context && <ContextChips context={message.context} />}
        {!busy && (
          <button type="button" class="icon-button subtle" title="Edit and resend" aria-label="Edit prompt" onClick={() => setEditing(true)}>
            <IconPencil size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

/** Seconds since `since`, ticking while mounted. */
function useElapsed(since: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return Math.max(0, Math.floor((now - since) / 1000));
}

/** States what the model is doing and for how long, instead of an idle animation. */
function Thinking({ since }: { since: number }) {
  const seconds = useElapsed(since);
  return (
    <p class="thinking" role="status">
      Thinking{seconds >= 2 ? ` · ${seconds}s` : '…'}
    </p>
  );
}

function LiveReasoningLabel({ since }: { since: number }) {
  const seconds = useElapsed(since);
  return <>Thinking{seconds >= 2 ? ` · ${seconds}s` : '…'}</>;
}

function Reasoning({ text, live, since }: { text: string; live: boolean; since: number }) {
  return (
    <details class="reasoning">
      <summary>{live ? <LiveReasoningLabel since={since} /> : 'Reasoning'}</summary>
      <Markdown source={text} class="reasoning-text" />
    </details>
  );
}

const fmtTokens = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k` : String(n));

function AssistantMessage({ message, isLast }: { message: CardMessage; isLast: boolean }) {
  const stream = streaming.value;
  const live = stream?.messageId === message.id;
  const showThinking = live && message.parts.length === 0 && !stream.tool;
  const usage = message.usage;
  return (
    <div class="message assistant">
      {message.parts.map((part, i) => {
        if (part.type === 'text') return <Markdown key={i} source={part.text} />;
        if (part.type === 'reasoning') {
          return <Reasoning key={i} text={part.text} live={Boolean(live && i === message.parts.length - 1)} since={stream?.startedAt ?? 0} />;
        }
        if (part.type === 'proposal') return <ProposalCard key={part.proposal.id} proposal={part.proposal} />;
        return (
          <div key={i} class="message-error" role="alert">
            <IconAlert size={16} />
            <span>{part.text}</span>
            {isLast && !stream && (
              <button type="button" class="button small" onClick={() => void retryLastTurn()}>
                <IconRefresh size={14} /> Retry
              </button>
            )}
          </div>
        );
      })}
      {showThinking && <Thinking since={stream.startedAt} />}
      {live && stream.tool && (
        <p class="tool-progress" role="status">
          {stream.tool.target === 'code' ? <IconCode size={14} /> : <IconBook size={14} />} Writing the {stream.tool.target} ·{' '}
          {plural(stream.tool.lines, 'line')}
        </p>
      )}
      {usage && usage.inputTokens + usage.outputTokens > 0 && (
        <p class="usage" title={usage.model ? `Answered by ${usage.model}. Tokens used by this reply.` : 'Tokens used by this reply'}>
          {usage.model ? `${modelLabel(message.native?.provider ?? settings.value.provider, usage.model)} · ` : ''}
          {fmtTokens(usage.inputTokens)} in{usage.cachedInputTokens ? ` (${fmtTokens(usage.cachedInputTokens)} cached)` : ''} ·{' '}
          {fmtTokens(usage.outputTokens)} out
        </p>
      )}
    </div>
  );
}

const STARTERS = [
  'Write a Python script that plots a damped sine wave with matplotlib',
  'Build an HTML page with a button that counts clicks',
  'Write a JavaScript debounce function, with tests',
];

function EmptyState() {
  const ready = providerReady.value;
  const hasCode = activeCode.value.trim() !== '';
  return (
    <div class="empty-state">
      <h2 class="empty-title">What's your cue?</h2>
      <p>
        Say what to build or change. Cue writes the code in the editor, keeps a spec beside it, and runs Python, JavaScript and
        HTML right here in your browser.
      </p>
      {!ready && (
        <>
          <div class="empty-actions">
            <button type="button" class="button primary" onClick={() => updateUi({ settingsOpen: true })}>
              Choose a model
            </button>
            <button
              type="button"
              class="button"
              onClick={() => {
                updateSettings({ provider: 'demo' });
                // The button goes away once a provider is ready; hand focus to the prompt instead of the page body.
                requestAnimationFrame(() => document.querySelector<HTMLTextAreaElement>('.composer textarea')?.focus());
              }}
            >
              Try the demo
            </button>
          </div>
          <p class="empty-note">No paid key? An OpenRouter key works with free models.</p>
        </>
      )}
      <p class="empty-label">{hasCode ? 'Start with your code, or an example' : 'Start with a cue'}</p>
      <div class="examples">
        {hasCode && (
          <button type="button" class="chip-button" onClick={() => void sendMessage('Explain what this code does, briefly.', { action: 'explain' })}>
            Explain this code
          </button>
        )}
        {STARTERS.map((example) => (
          <button key={example} type="button" class="chip-button" onClick={() => void sendMessage(example, { action: 'starter' })}>
            {example}
          </button>
        ))}
      </div>
    </div>
  );
}

function Suggestions() {
  const card = activeCard.value;
  const current = suggestions.value;
  if (!card || !current || current.cardId !== card.id || streaming.value) return null;
  if (current.status === 'ready' && current.items.length === 0) return null;
  return (
    <div class="suggestions" aria-label="Suggested next prompts" aria-busy={current.status === 'loading'}>
      <span class="suggestions-label">Next cues</span>
      {current.status === 'loading' && current.items.length === 0
        ? [0, 1, 2].map((i) => <span key={i} class="chip-skeleton" aria-hidden="true" />)
        : current.items.map((item) => (
            <button key={item} type="button" class="chip-button" onClick={() => void sendSuggestion(item)}>
              {item}
            </button>
          ))}
    </div>
  );
}

/** Toggles for what rides along with the next prompt: the code, the spec, and the last run's output. */
function Attachments() {
  const card = activeCard.value;
  const a = attach.value;
  void outputItems.value;
  if (!card) return null;
  const code = activeCode.value;
  const spec = activeSpec.value;
  const hasOutput = outputItems.value.length > 0;
  const state = specState({ code, spec, specSync: card.specSync });
  const chip = (kind: 'code' | 'spec' | 'output', label: string, detail: string, icon: ComponentChildren) => (
    <button
      type="button"
      class="attach-chip"
      aria-pressed={a[kind]}
      title={a[kind] ? `${label} is sent with your prompt. Click to leave it out.` : `${label} is left out. Click to send it.`}
      onClick={() => toggleAttachment(kind)}
    >
      {a[kind] ? icon : <IconPlus size={13} />}
      <span>{label}</span>
      <span class="attach-detail">{detail}</span>
    </button>
  );
  return (
    <div class="attachments" aria-label="Sent with your prompt">
      {chip('code', languageInfo(card.language).label, code.trim() ? plural(lineCount(code), 'line') : 'empty', <IconCode size={13} />)}
      {chip('spec', 'Spec', spec.trim() ? SPEC_STATE_LABEL[state].toLowerCase() : 'none yet', <IconBook size={13} />)}
      {hasOutput && chip('output', 'Output', runFailed.value ? 'failed run' : 'last run', <IconTerminal size={13} />)}
    </div>
  );
}

export function ConversationPanel() {
  const card = activeCard.value;
  const stream = streaming.value;
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const stickToBottom = useRef(true);
  const messages = card?.messages ?? [];
  const lastMessage = messages[messages.length - 1];

  useEffect(() => {
    const list = listRef.current;
    if (list && stickToBottom.current) list.scrollTop = list.scrollHeight;
  });

  useEffect(() => {
    stickToBottom.current = true;
    inputRef.current?.focus();
  }, [card?.id]);

  const onScroll = () => {
    const list = listRef.current;
    if (!list) return;
    stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight < 80;
  };

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    // The composer grows while typing; bring it back to one line once the prompt is sent.
    if (inputRef.current) inputRef.current.style.height = '';
    stickToBottom.current = true;
    void sendMessage(text).then((sent) => {
      // Keep what the user typed when it could not be sent (for example, no provider configured yet).
      if (!sent) setDraft((current) => current || text);
    });
  };

  return (
    <section class="conversation" aria-label="Conversation">
      <div class="message-list" ref={listRef} onScroll={onScroll}>
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          messages.map((m) =>
            m.role === 'user' ? <UserMessage key={m.id} message={m} /> : <AssistantMessage key={m.id} message={m} isLast={m === lastMessage} />,
          )
        )}
      </div>
      <Suggestions />
      <form
        class="composer"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <textarea
          ref={inputRef}
          value={draft}
          rows={1}
          placeholder="Ask about the code, or say what to build"
          aria-label="Prompt"
          onInput={(e) => {
            const el = e.target as HTMLTextAreaElement;
            setDraft(el.value);
            el.style.height = 'auto';
            el.style.height = `${Math.min(200, el.scrollHeight)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <div class="composer-row">
          <Attachments />
          {stream ? (
            <button type="button" class="icon-button send stop" title="Stop" aria-label="Stop" onClick={stopStreaming}>
              <IconStop />
            </button>
          ) : (
            <button type="submit" class="icon-button send" title="Send (Enter)" aria-label="Send" disabled={!draft.trim()}>
              <IconSend />
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
